import { AccountStatus, AccountType } from "@/modules/account/common/enum";
import type { IAccount } from "@/modules/account/common/types";
import { useGetAccountsQuery } from "@/modules/account/services";
import { setShowBalances } from "@/modules/dashboard/state";
import { INITIAL_PAGE } from "@/shared/common/constants";
import { AppPaths } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { PageWidgetTemplate } from "@/shared/components";
import { useAppDispatch, useAppSelector, useShowBalance } from "@/shared/hooks";
import { setAccountActiveTab, setActiveAccountId } from "@/shared/state/common";
import classNames from "classnames";
import { type Dispatch, type SetStateAction, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AccountItem } from "./AccountItem";

interface Props {
  limit?: number;
  type?: string;
  status?: string;
  accountId?: string | number;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

export const Accounts = ({
  limit = 100,
  type = AccountType.SettlementAccount,
  status = AccountStatus.Open,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const activeItem = useAppSelector(state => state.common);
  const { handleShowBalances } = useShowBalance();

  const {
    data: accountList,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetAccountsQuery({
    pageIndex: INITIAL_PAGE,
    pageSize: limit,
    accountType: type,
    accountStatus: status,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const onShowDetails = (account: IAccount) => {
    dispatch(setAccountActiveTab(account.subType || ""));
    dispatch(setActiveAccountId(account.id));

    if (!location.pathname.includes(AppPaths.Accounts)) {
      navigate(AppPaths.Accounts);
    }
  };

  useEffect(() => {
    if (accountList) {
      const showBalances = Array(accountList?.length).fill(false);
      dispatch(setShowBalances(showBalances));
    }
  }, [accountList]);

  return (
    <PageWidgetTemplate
      isEmpty={!accountList?.length}
      isFetching={isFetching}
      isError={isError}
      error={error as IError}
      refetch={refetch}
    >
      {accountList?.map((account, i) => (
        <div key={account.id} onClick={() => onShowDetails(account)}>
          <AccountItem
            {...account}
            canToggleBalance
            className={
              classNames("pl-32 pr-32 widget-content-item", {
                "selected": activeItem?.accountId === account.id
              })
            }
            handleShowBalances={handleShowBalances}
            index={i}
          />
        </div>
      ))}
    </PageWidgetTemplate>
  );
};
